/**
 * Founder/admin memorial product request queue — Wave 4C1 lite.
 * Payment, proof approval, shipping and fulfilment are not live yet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardList,
  ImageIcon,
  Loader2,
  RefreshCw,
  Save,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getBackendBaseUrl } from '../utils/apiBase';
import { isFounderAdmin } from '../utils/adminAccess';
import { humanizeError } from '../utils/errorHandling';
import {
  ADMIN_QUEUE_STATUSES,
  adminQueueStatusCopy,
  getAdminMemorialProductRequest,
  listAdminMemorialProductRequests,
  updateAdminMemorialProductRequestNotes,
  updateAdminMemorialProductRequestStatus,
} from '../utils/adminMemorialProductRequests';

const BACKEND = getBackendBaseUrl();

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function StatusBadge({ status }) {
  const copy = adminQueueStatusCopy(status);
  return (
    <Badge variant="secondary" className="rounded-full text-[10px] uppercase tracking-wide">
      {copy.label}
    </Badge>
  );
}

export default function AdminProductRequestsPage() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [statusDraft, setStatusDraft] = useState('submitted');
  const [notesDraft, setNotesDraft] = useState('');
  const [error, setError] = useState(null);

  const loadList = useCallback(async () => {
    if (!isFounderAdmin(user)) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await listAdminMemorialProductRequests({
        status: statusFilter || undefined,
      });
      setItems(rows);
      if (selectedId && !rows.some((r) => r.id === selectedId)) {
        setSelectedId(null);
        setDetail(null);
      }
    } catch (err) {
      setError(humanizeError(err));
    } finally {
      setLoading(false);
    }
  }, [user, statusFilter, selectedId]);

  const loadDetail = useCallback(
    async (requestId) => {
      if (!requestId || !isFounderAdmin(user)) return;
      setDetailLoading(true);
      setError(null);
      try {
        const row = await getAdminMemorialProductRequest(requestId);
        setDetail(row);
        setStatusDraft(row.status || 'submitted');
        setNotesDraft(row.admin_notes || '');
      } catch (err) {
        setError(humanizeError(err));
      } finally {
        setDetailLoading(false);
      }
    },
    [user],
  );

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const handleSelect = (id) => {
    setSelectedId(id);
  };

  const handleStatusSave = async () => {
    if (!selectedId) return;
    setSavingStatus(true);
    setError(null);
    try {
      const updated = await updateAdminMemorialProductRequestStatus(selectedId, statusDraft);
      setDetail(updated);
      await loadList();
    } catch (err) {
      setError(humanizeError(err));
    } finally {
      setSavingStatus(false);
    }
  };

  const handleNotesSave = async () => {
    if (!selectedId) return;
    setSavingNotes(true);
    setError(null);
    try {
      const updated = await updateAdminMemorialProductRequestNotes(selectedId, notesDraft);
      setDetail(updated);
    } catch (err) {
      setError(humanizeError(err));
    } finally {
      setSavingNotes(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isFounderAdmin(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-product-requests-page">
      <header className="border-b border-border/40 bg-card/40">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Button type="button" variant="ghost" size="sm" className="rounded-full" asChild>
              <Link to="/dashboard">
                <ArrowLeft className="mr-1 h-4 w-4" />
                Dashboard
              </Link>
            </Button>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                Internal · Founder
              </p>
              <h1 className="font-serif text-xl font-light text-foreground">Memorial Product Requests</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Review saved keepsake requests before payment, proofing and fulfilment are enabled.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="rounded-full" asChild>
              <Link to="/admin/insights">Product insights</Link>
            </Button>
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={loadList}>
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-muted-foreground">
          <strong className="font-medium text-foreground">Request queue only.</strong> Payment and fulfilment are
          not live yet. Use this queue to review submitted keepsake requests — no checkout, shipping or proof
          approval workflow.
        </div>

        {error ? (
          <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <label className="text-xs text-muted-foreground" htmlFor="mpr-status-filter">
            Status filter
          </label>
          <select
            id="mpr-status-filter"
            className="rounded-lg border border-border/60 bg-background px-3 py-1.5 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            {ADMIN_QUEUE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {adminQueueStatusCopy(s).label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-6 lg:grid-cols-5">
          <section className="lg:col-span-2 rounded-2xl border border-border/60 bg-card/50 p-4">
            <div className="mb-3 flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-serif text-lg font-light">Request queue</h2>
            </div>
            {loading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : items.length === 0 ? (
              <p className="text-sm text-muted-foreground">No saved keepsake requests yet.</p>
            ) : (
              <ul className="space-y-2">
                {items.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(row.id)}
                      className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                        selectedId === row.id
                          ? 'border-primary/50 bg-primary/5'
                          : 'border-border/50 bg-background hover:border-border'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {row.product_label || row.product_type}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {row.person_name || 'Person'} · {formatDate(row.created_at)}
                          </p>
                          <p className="mt-1 text-[10px] font-mono text-muted-foreground truncate">
                            {row.requester_email || row.user_id}
                          </p>
                        </div>
                        <StatusBadge status={row.status} />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="lg:col-span-3 rounded-2xl border border-border/60 bg-card/50 p-4 sm:p-5">
            {!selectedId ? (
              <p className="text-sm text-muted-foreground">Select a request to review details.</p>
            ) : detailLoading && !detail ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : detail ? (
              <div className="space-y-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-serif text-lg font-light text-foreground">
                      {detail.product_label || detail.product_type}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Saved for review · {formatDate(detail.created_at)}
                    </p>
                  </div>
                  <StatusBadge status={detail.status} />
                </div>

                {detail.keepsake_image_url ? (
                  <div className="overflow-hidden rounded-xl border border-border/50 bg-muted/20">
                    <img
                      src={
                        detail.keepsake_image_url.startsWith('http')
                          ? detail.keepsake_image_url
                          : `${BACKEND}${detail.keepsake_image_url}`
                      }
                      alt="Keepsake preview"
                      className="max-h-64 w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 rounded-xl border border-dashed border-border/60 px-4 py-8 text-sm text-muted-foreground">
                    <ImageIcon className="h-4 w-4" />
                    No image preview available
                  </div>
                )}

                <dl className="grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Requester</dt>
                    <dd>{detail.requester_email || detail.user_id}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Person</dt>
                    <dd>{detail.person_name || detail.person_id}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Memorial</dt>
                    <dd>{detail.memorial_title || detail.memorial_id}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Archive / tree</dt>
                    <dd className="font-mono text-xs">{detail.tree_id || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Size / frame</dt>
                    <dd>
                      {detail.size} · {detail.orientation} · {detail.frame_colour}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Image source</dt>
                    <dd>{detail.image_source || '—'}</dd>
                  </div>
                </dl>

                {detail.tribute_text ? (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Tribute text (submitted)</p>
                    <p className="mt-1 rounded-lg border border-border/40 bg-background px-3 py-2 text-sm">
                      {detail.tribute_text}
                    </p>
                  </div>
                ) : null}

                <div className="rounded-xl border border-border/50 bg-background/60 p-4">
                  <p className="mb-2 text-sm font-medium text-foreground">Internal status</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      className="rounded-lg border border-border/60 bg-background px-3 py-1.5 text-sm"
                      value={statusDraft}
                      onChange={(e) => setStatusDraft(e.target.value)}
                    >
                      {ADMIN_QUEUE_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {adminQueueStatusCopy(s).label}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      size="sm"
                      disabled={savingStatus || statusDraft === detail.status}
                      onClick={handleStatusSave}
                    >
                      {savingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
                      Update status
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {adminQueueStatusCopy(statusDraft).description}
                  </p>
                </div>

                <div className="rounded-xl border border-border/50 bg-background/60 p-4">
                  <p className="mb-2 text-sm font-medium text-foreground">Internal notes</p>
                  <p className="mb-2 text-xs text-muted-foreground">Founder-only — not visible to customers.</p>
                  <textarea
                    className="min-h-[96px] w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm"
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder="Operational notes for this request…"
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="mt-2"
                    disabled={savingNotes || notesDraft === (detail.admin_notes || '')}
                    onClick={handleNotesSave}
                  >
                    {savingNotes ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
                    Save notes
                  </Button>
                </div>

                {detail.status_history?.length ? (
                  <div>
                    <p className="mb-2 text-sm font-medium text-foreground">Status history</p>
                    <ul className="space-y-1 text-xs text-muted-foreground">
                      {detail.status_history.map((entry, idx) => (
                        <li key={`${entry.at}-${idx}`}>
                          {adminQueueStatusCopy(entry.status).label} · {formatDate(entry.at)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>
        </div>
      </main>
    </div>
  );
}
